export interface TemporaryAccessInput {
  billingId?: string | null
  active?: boolean | null
  // A timestamp in CouchDB; the clients index may return it as a date string.
  trialExpiry?: number | string | null
}

/**
 * Whether a deactivated, billed company is inside a temporary access window (ENG-2358).
 *
 * When a cancelled or paused customer asks to get back in "to see one last thing", support sets the
 * company's Trial Expiry to a date in the future. The company keeps its `billingId` and stays
 * `active: false`, and gets in until that date passes, with no one having to remember to lock it
 * again.
 *
 * Keyed on `active === false`, not on a list of Chargebee statuses: `cancelled` and `paused` both
 * deactivate a company, and `non_renewing` keeps it active until its term ends, so it never needs
 * a window.
 *
 * This is not a trial. A company with a `billingId` is never `isTrial`, so it sees no trial banner
 * or checkout prompts during the window.
 */
export const hasTemporaryAccess = (meta: TemporaryAccessInput | null | undefined, now = Date.now()): boolean => {
  if (!meta?.billingId || meta.active !== false || !meta.trialExpiry) {
    return false
  }
  const expiry = new Date(meta.trialExpiry).getTime()
  return !Number.isNaN(expiry) && expiry > now
}
